/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { selectCurrentUserApiToken, selectCurrentUserName } from "../slices/currentUser/currentUserSlice";
import getFhirJson from "../functions/getFhirJsonAPI";
import { Button, Segment, Tab, TextArea } from "semantic-ui-react";
import ProfilesByResourceType from "../dictionaries/ProfilesByResourceType";
import StringEntry from "../datatypeComponents/StringEntry";
import CodeEntry from "../datatypeComponents/CodeEntry";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import submitResource from "./resourceDisplayCommonFunctions/submitResource";
import GenericToggleButtons from "../components/GenericToggleButtons";
import { fevirPublicationStatusActive, fevirPublicationStatusDraft } from "../dictionaries/THOandFHIRValueSets";
import addProfileValuesToNewResource from "./profileFunctions/addProfileValuesToNewResource";
import createJsonForNewResource from "./profileFunctions/createJsonForNewResource";

const ResourceProfileRadioGroup = ({ newResourceState, setNewResourceState }) => {
    return <>
        {ProfilesByResourceType[newResourceState?.resourceType] &&
            <div style={{ marginLeft: "24px" }}>
                <p style={{ marginBottom: "8px" }}><br />Select a Profile</p>
                <RadioGroup aria-label='profile-selection'
                    name='profile-selection' value={newResourceState.profileSelected || ""} >
                    {Object.keys(ProfilesByResourceType[newResourceState.resourceType]).map((profile, profileIndex) => {
                        const description = ProfilesByResourceType[newResourceState.resourceType][profile].description;
                        const htmlDescription = ProfilesByResourceType[newResourceState.resourceType][profile].htmlDescription;
                        if (description) {
                            return <p key={profileIndex} style={{ marginBottom: "0px" }} >
                                <FormControlLabel
                                    key={profileIndex}
                                    value={profile}
                                    control={<Radio color="primary"
                                        onClick={(e) => {
                                            if (newResourceState.profileSelected === e.target.value) {
                                                addProfileValuesToNewResource(newResourceState.resourceType, "", setNewResourceState);
                                            } else {
                                                addProfileValuesToNewResource(newResourceState.resourceType, e.target.value, setNewResourceState);
                                            }
                                        }} />}
                                    name="profile-selection-radio-button-control"
                                    color="default"
                                    inputprops={{ 'aria-label': profile }}
                                />
                                <span className="unselectable" style={{ cursor: "pointer" }}
                                    onClick={() => {
                                        addProfileValuesToNewResource(newResourceState.resourceType, profile, setNewResourceState);
                                    }}>{htmlDescription ? htmlDescription : description}</span>
                            </p>
                        } else {
                            return <p key={profileIndex} style={{ marginBottom: "8px" }} ><br /></p>
                        }
                    })}
                </RadioGroup>
            </div>}
    </>
}

const NewResourceEntry = ({ newResourceState, setNewResourceState, resourceTypeList,
    titleOnly, profileSelected, resourceType }) => {

    if (!resourceTypeList) {
        resourceTypeList = ['ActivityDefinition', 'ArtifactAssessment', 'Bundle', 'Citation',
            'CodeSystem', 'Composition', 'DocumentReference', 'Evidence', 'EvidenceVariable', 'Group',
            'Library', 'List', 'Measure',
            'PlanDefinition', 'ResearchStudy', 'ValueSet'];
    }

    useEffect(() => {
        if (resourceType && !newResourceState.resourceType) {
            setNewResourceState(prevState => { return { ...prevState, resourceType: resourceType } });
        }
        if (resourceType || profileSelected) {
            addProfileValuesToNewResource(resourceType, profileSelected, setNewResourceState);
        }
    }, [resourceType, profileSelected, setNewResourceState])


    if (titleOnly) {
        let itemTypeDisplay = newResourceState.resourceType + " Resource";
        if (newResourceState.meta?.profile) {
            for (const profile of newResourceState.meta.profile) {
                const profileDictionary = ProfilesByResourceType[newResourceState.resourceType];
                for (const key in profileDictionary) {
                    if (profileDictionary[key].url === profile) {
                        itemTypeDisplay = key;
                        break;
                    }
                }
            }
        }
        return <div>
            <div style={{ marginLeft: "24px" }}>
                <p>Enter a title here and click Submit to create a new blank {itemTypeDisplay}.</p>
                <StringEntry elementName='title' fieldLabel='Title'
                    startingValue={newResourceState.title} setResourceState={setNewResourceState} />
            </div>
        </div>
    }

    return <div>
        <div style={{ marginLeft: "24px" }}>
            <p><b>Enter a title here, select a Resource type, optionally select a Profile, and click Submit to create a new blank Resource.</b></p>
            <p>Alternatively, you can paste and/or edit complete JSON in the JSON View tab. Use of data entry fields in the Text View will replace the JSON.</p>
            <StringEntry elementName='title' fieldLabel='Title'
                startingValue={newResourceState.title} setResourceState={setNewResourceState} />
            <br /><br />
            <CodeEntry elementName='resourceType' fieldLabel='Resource Type'
                allowedValues={resourceTypeList}
                startingValue={resourceType || newResourceState.resourceType} setResourceState={setNewResourceState} />
            {newResourceState.resourceType && <div>
                {newResourceState.resourceType === 'ActivityDefinition' && <p>
                    The ActivityDefinition Resource allows for the definition of some activity to be performed, independent of a particular patient, practitioner, or other performance context.
                </p>}
                {newResourceState.resourceType === 'ArtifactAssessment' && <div>
                    <p>The ArtifactAssessment Resource provides one or more comments, classifiers or ratings about a Resource and supports attribution and rights management metadata for the added content.</p>
                </div>}
                {newResourceState.resourceType === 'Citation' && <div>
                    <p>The Citation Resource enables reference to any knowledge artifact for purposes of identification and attribution.</p>
                </div>}
                {newResourceState.resourceType === 'CodeSystem' && <p>
                    The CodeSystem Resource is used to declare the existence of and describe a code system (structured terminology) or code system supplement and its key properties, and optionally define a part or all of its content.
                </p>}
                {newResourceState.resourceType === 'Composition' && <div>
                    <p>
                        The Composition Resource provides a package of content and defines the structure and narrative content necessary for a document.
                    </p>
                </div>}
                {newResourceState.resourceType === 'Evidence' && <div>
                    <p>
                        The Evidence Resource provides a machine-interpretable expression of an evidence concept including the evidence variables (e.g., population, exposures/interventions, comparators, outcomes, measured variables, confounding variables), the statistics, and the certainty of this evidence.
                    </p>
                </div>}
                {newResourceState.resourceType === 'EvidenceVariable' && <div>
                    <p>The EvidenceVariable Resource describes an element that knowledge (Evidence) is about.</p>
                </div>}
                {newResourceState.resourceType === 'Group' && <div>
                    <p>
                        The Group Resource represents a defined collection of entities that may be discussed or acted upon collectively but which are not expected to act collectively, and are not formally or legally recognized; i.e. a collection of entities that isn't an Organization.
                    </p>
                </div>}
                {newResourceState.resourceType === 'Library' && <div>
                    <p>
                        The Library Resource is a container for a collection of knowledge assets.
                    </p>
                </div>}
                {newResourceState.resourceType === 'List' && <div>
                    <p>
                        The List Resource represents a curated collection of resources.
                    </p>
                </div>}
                {newResourceState.resourceType === 'Measure' && <p>
                    The Measure Resource allows for the definition of a quality measure or performance measure.
                </p>}
                {newResourceState.resourceType === 'PlanDefinition' && <div>
                    <p>
                        The PlanDefinition Resource allows for the definition of various types of plans as a sharable, consumable, and executable artifact. The resource is general enough to support the description of a broad range of clinical and non-clinical artifacts such as clinical decision support rules, order sets, protocols, and drug quality specifications.
                    </p>
                </div>}
                {newResourceState.resourceType === 'Project' && <p>
                    The Project Resource is not a FHIR Resource. The Project Resource is used to store the data for Projects on the FEvIR Platform.
                </p>}
                {newResourceState.resourceType === 'ResearchStudy' && <div>
                    <p>
                        The ResearchStudy Resource allows for the definition of a scientific study.
                    </p>
                </div>}
                {newResourceState.resourceType === 'ValueSet' && <p>
                    The ValueSet Resource instance specifies a set of codes (terms) drawn from one or more code systems (structured terminologies), intended for use in a particular context.
                </p>}
                <ResourceProfileRadioGroup newResourceState={newResourceState} setNewResourceState={setNewResourceState} />
            </div>}
        </div>
    </div>
}
let tabPanesLookup = { 'Text View': null, 'JSON View': null };

let tabPanes = Object.keys(tabPanesLookup).map((tab, index) => {
    tabPanesLookup[tab] = index;
    let tabObj = {
        menuItem: tab
    };
    return tabObj;
});

const ViewTabPane = ({ fhirEntryState, setFhirEntryState }) => (<Tab menu={{ secondary: true, pointing: true }}
    panes={tabPanes} activeIndex={fhirEntryState.activeIndex}
    onTabChange={(e, data) => {
        setFhirEntryState(prevState => {
            return {
                ...prevState,
                "activeIndex": data.activeIndex
            };
        });
    }}
/>);


const createClonedResource = async (apiToken, resourceId, userName, resourceType, setFhirEntryState) => {
    if (apiToken) {
        if (resourceId) {
            let fhirJson = await getFhirJson(resourceId, null, apiToken);
            let originalTitle = "";
            if (fhirJson.title && typeof fhirJson.title === "string" && fhirJson.title.trim()) {
                originalTitle = fhirJson.title;
                fhirJson.title = fhirJson.title + " - " + userName;
            } else if (fhirJson.name) {
                if (typeof fhirJson.name === "string" && fhirJson.name.trim()) {
                    fhirJson.name = fhirJson.name + " - " + userName;
                    originalTitle = fhirJson.name;
                } else if (Array.isArray(fhirJson.name) && fhirJson.name.length > 0 &&
                    fhirJson.name[0].text && fhirJson.name[0].text.trim() !== "") {
                    fhirJson.name.append = { "text": fhirJson.name[0].text + " - " + userName };
                    originalTitle = fhirJson.name[0].text;
                }
            }
            if (fhirJson.author) {
                delete fhirJson.author;
            }
            let identifiersToKeep = [];
            if (fhirJson.identifier) {
                for (let identifierIndex in fhirJson.identifier) {
                    if (fhirJson.identifier[identifierIndex].system !== "https://fevir.net") {
                        identifiersToKeep.push(fhirJson.identifier[identifierIndex]);
                    }
                }
                delete fhirJson.identifier;
                if (identifiersToKeep.length > 0) {
                    fhirJson.identifier = identifiersToKeep;
                }
            }
            let relatedArtifactEntry = {
                "type": "derived-from",
                "resourceReference": {
                    "reference": resourceType + "/" + resourceId,
                    "type": resourceType,
                    "display": originalTitle
                }
            }
            let relatesToEntry = {
                "type": "derived-from",
                "targetReference": {
                    "reference": resourceType + "/" + resourceId,
                    "type": resourceType,
                    "display": originalTitle
                }
            }
            if (resourceType === "Composition" || resourceType === "ArtifactAssessment" ||
                resourceType === "Evidence" || resourceType === "EvidenceVariable" ||
                resourceType === "Citation" || resourceType === "ResearchStudy"
            ) {
                if (!fhirJson.relatesTo) {
                    fhirJson.relatesTo = [];
                }
                fhirJson.relatesTo.push(relatesToEntry);
            } else if (resourceType === "ActivityDefinition" || resourceType === "CodeSystem" ||
                resourceType === "ConceptMap" || resourceType === "DeviceDefinition" ||
                resourceType === "EventDefinition" || resourceType === "Library" ||
                resourceType === "Measure" || resourceType === "PlanDefinition" ||
                resourceType === "ValueSet") {
                if (!fhirJson.relatedArtifact) {
                    fhirJson.relatedArtifact = [];
                }
                fhirJson.relatedArtifact.push(relatedArtifactEntry);
            } else if (resourceType === "Group") {
                if (!fhirJson.extension) {
                    fhirJson.extension = [];
                }
                fhirJson.extension.push({
                    url: "http://hl7.org/fhir/uv/ebm/StructureDefinition/relates-to",
                    extension: [
                        { "url": "type", "valueCode": relatesToEntry.type },
                        { "url": "targetReference", "valueReference": relatesToEntry.targetReference }
                    ]
                });
            } else {
                if (!fhirJson.extension) {
                    fhirJson.extension = [];
                }
                fhirJson.extension.push({
                    url: "http://hl7.org/fhir/StructureDefinition/artifact-relatedArtifact",
                    valueRelatedArtifact: relatedArtifactEntry
                });
            }
            setFhirEntryState(prevState => {
                return {
                    ...prevState,
                    "fhirEntry": JSON.stringify(fhirJson)
                };
            });
            window.history.replaceState(null, "", "/create");
        }
    }
}

const CreateResourcePage = ({ resourceStarter }) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { resourcetype, foi } = useParams();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const userName = useSelector(selectCurrentUserName);

    let resourceType = resourcetype;
    let resourceId = foi;
    let jsonView = false;
    let profileSelected;
    if (resourceId && isNaN(resourceId)) {
        profileSelected = resourceId; //This is becuase the 'foi' param can be used as a profile selector if using a string instead of a number
    } else if (resourceType && resourceId) {
        jsonView = true;
    }

    if (window.location.hash === "#json") {
        jsonView = true;
    }

    let titleOnly = true;
    if (profileSelected && !resourceStarter) {
        titleOnly = false;
        resourceStarter = { "resourceType": resourceType, "title": "" };
        resourceStarter.profileSelected = profileSelected;
    }
    if (!resourceStarter) {
        titleOnly = false;
        resourceStarter = { "resourceType": "", "title": "" };
    }

    const [fhirEntryState, setFhirEntryState] = useState({
        "fhirEntry": "",
        "status": "active",
        "activeIndex": (!isNaN(resourceId) || jsonView) ? 1 : 0
    });

    const [newResourceState, setNewResourceState] = useState(resourceStarter);
    const [publicationStatusState, setPublicationStatusState] = useState({"status": "active"});

    useEffect(() => {
        dispatch(changeBrowserTabTitle("FEvIR Resource Entry"));
        createClonedResource(apiToken, resourceId, userName, resourceType, setFhirEntryState);
    }, []);

    useEffect(() => {
        if (publicationStatusState.status && fhirEntryState.fhirEntry) {
            let newPublicationStatus = fevirPublicationStatusActive;
            if (publicationStatusState.status === "draft") {
                newPublicationStatus = fevirPublicationStatusDraft;
            }
            let newFhirJson = JSON.parse(fhirEntryState.fhirEntry);
            if (newFhirJson.extension) {
                let publicationExtensionFound = false;
                for (let extension of newFhirJson.extension) {
                    if (extension.url === "http://hl7.org/fhir/uv/ebm/StructureDefinition/artifact-publication-status") {
                        extension.valueCodeableConcept = newPublicationStatus;
                        publicationExtensionFound = true;
                    }
                }
                if (!publicationExtensionFound) {
                    newFhirJson.extension.push({
                        "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/artifact-publication-status",
                        "valueCodeableConcept": newPublicationStatus
                    });
                }
            } else {
                newFhirJson.extension = [{
                    "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/artifact-publication-status",
                    "valueCodeableConcept": newPublicationStatus
                }]
            }
            let newFhirEntry = JSON.stringify(newFhirJson);
            setFhirEntryState(prevState => { return { ...prevState, "fhirEntry": newFhirEntry }; });
        }
    }, [publicationStatusState]);

    useEffect(() => {
        if (JSON.stringify(newResourceState) !== JSON.stringify({ "resourceType": "", "title": "" })) {
            let newJson = createJsonForNewResource(newResourceState);
            setFhirEntryState(prevState => {
                return {
                    ...prevState,
                    "fhirEntry": JSON.stringify(newJson)
                };
            });
        }
    }, [newResourceState]);

    let fhirString = typeof (fhirEntryState.fhirEntry) === "string" ?
        fhirEntryState.fhirEntry : JSON.stringify(fhirEntryState.fhirEntry);

    return <div>
        <Segment className={`containerSegment maxRemainderOfPageSegment`} raised>
            <ViewTabPane fhirEntryState={fhirEntryState} setFhirEntryState={setFhirEntryState} />
            <br /><br />
            {apiToken ?
                <>
                    {(tabPanes[fhirEntryState.activeIndex]["menuItem"] === "Text View") ?
                        <>
                            <NewResourceEntry newResourceState={newResourceState}
                                setNewResourceState={setNewResourceState}
                                profileSelected={profileSelected} resourceType={resourceType}
                                titleOnly={titleOnly} />
                            <br /> <br />
                            {titleOnly === false &&
                                <>
                                    <GenericToggleButtons thin={true} boxes={true} togglable={false}
                                        values={["active", "draft"]}
                                        labels={["Publish (anyone can view)", "Draft (only editors can view)"]}
                                        fieldLabel="status" elementName="status"
                                        startingValue={publicationStatusState.status} setter={setPublicationStatusState}
                                    />
                                    <br />
                                    <br />
                                </>}
                            <Button className="formButton positive" content="Submit" compact
                                onClick={() => {
                                    submitResource(fhirEntryState.fhirEntry, apiToken,
                                        navigate, dispatch, publicationStatusState.status);
                                }}
                                disabled={!newResourceState.title && !newResourceState.name} />
                        </>
                        :
                        <>
                            <TextArea placeholder='Paste your JSON here' style={{ minHeight: 100, width: "60%" }}
                                value={fhirString}
                                onChange={(e) => {
                                    setFhirEntryState(prevState => {
                                        return {
                                            ...prevState,
                                            "fhirEntry": e.target.value
                                        };
                                    });
                                }} />
                            <br /><br />
                            <GenericToggleButtons thin={true} boxes={true} togglable={false}
                                values={["active", "draft"]}
                                labels={["Publish (anyone can view)", "Draft (only editors can view)"]}
                                fieldLabel="status" elementName="status"
                                startingValue={publicationStatusState.status} setter={setPublicationStatusState}
                            />
                            <br />
                            <br />
                            <Button className="formButton positive" content="Submit" compact
                                onClick={() => {
                                    submitResource(fhirEntryState.fhirEntry, apiToken,
                                        navigate, dispatch, publicationStatusState.status);
                                }} />
                            {titleOnly === false && <>
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content="Use JSON Tree Writer for Evidence Resource" compact
                                    onClick={() => {
                                        window.open('https://khalid-shahin.github.io/JSON-Tree-Writer/EBMonFHIR-Evidence.html');
                                    }} /> {/**TODO -- remove or revise JSON Tree Writer function for new platform/current R6 structures */}
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content="Use JSON Tree Writer for EvidenceVariable Resource" compact
                                    onClick={() => {
                                        window.open('https://khalid-shahin.github.io/JSON-Tree-Writer/EBMonFHIR-EvidenceVariable.html');
                                    }} />
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content="JSON Bulk Entry" compact
                                    onClick={() => { navigate(`/jsonbulkentry`); }} />
                            </>}
                        </>
                    }
                </>
                :
                <p>Please login. (If you do not have an account click Sign In and log in with your Google Account.)</p>
            }
        </Segment>
    </div>
};

export default CreateResourcePage;